import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, WORKGROUP_KEY } from "@/lib/api";
import { useAuth, formatError } from "@/lib/auth";

export const inviteToken = () => new URLSearchParams(window.location.search).get("invite");

const clearInviteParam = () => window.history.replaceState(null, "", window.location.pathname);

export default function InvitePage({ token }) {
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.inviteInfo(token).then(setInfo).catch((e) => setInfo({ error: formatError(e) }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError("Passwords don't match");
    setBusy(true);
    setError(null);
    try {
      const u = await api.acceptInvite(token, { name: form.name, email: form.email, password: form.password });
      localStorage.setItem(WORKGROUP_KEY, u.workgroup_ids[0]);
      clearInviteParam();
      await refresh();
    } catch (err) {
      setError(formatError(err));
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="invite-page">
      <Card className="w-full max-w-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <img src="/morrhus-logo.png" alt="Morrstofa" className="h-11 w-auto" />
            <div>
              <h1 className="text-xl font-semibold">Join Morrstofa</h1>
              <p className="text-xs text-muted-foreground">Warmachine MK4 playtest workspace</p>
            </div>
          </div>
          {!info && <p className="text-sm text-muted-foreground">Checking invite…</p>}
          {info?.error && (
            <div className="space-y-3">
              <p className="text-sm text-rose-300" data-testid="invite-invalid">{info.error}</p>
              <Button variant="outline" className="w-full" onClick={() => { clearInviteParam(); window.location.reload(); }} data-testid="invite-go-login-btn">Go to sign in</Button>
            </div>
          )}
          {info && !info.error && (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-sm text-muted-foreground" data-testid="invite-groups">
                You're joining as a playtester in <span className="text-foreground">{info.workgroup_names.join(", ")}</span>.
              </p>
              <Input placeholder="Your name" value={form.name} onChange={set("name")} required data-testid="invite-accept-name-input" />
              <Input type="email" autoComplete="username" placeholder="Email" value={form.email} onChange={set("email")} required data-testid="invite-accept-email-input" />
              <Input type="password" autoComplete="new-password" placeholder="Password (8+ characters)" value={form.password} onChange={set("password")} required minLength={8} data-testid="invite-accept-password-input" />
              <Input type="password" autoComplete="new-password" placeholder="Repeat password" value={form.confirm} onChange={set("confirm")} required data-testid="invite-accept-confirm-input" />
              {error && <p className="text-sm text-rose-300" data-testid="invite-accept-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="invite-accept-submit-btn">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Create my account
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
